import React, { useEffect, useState } from 'react';
import CommonModal from '../../components/CommonModal';
import { IWoorimInitState } from '../../interfaces/common';
import styled, { css } from 'styled-components';
import { Flex, FlexColumn, Text } from '../../assets/styles';
import { useSelector } from 'react-redux';
import CommonInput from '../../components/CommonInput';
import CommonButton from '../../components/CommonButton';
import EmailVO, { ICirculars, ILocation, IType } from '../../interfaces/EmailVO';
import SelectCountry from '../search/SelectCountry';
import CommonSelect2, { IOptionObj } from '../../components/CommonSelect2';
import SelectArea from '../search/SelectArea';
import { Spacing } from '../../components/Common';
import { delEmail, getEmail, postEmail, putEmail } from '../../apis/emailService';
import CommonCheckbox from '../../components/CommonCheckbox';
import ConfirmModal from './ConfirmModal';
import Quill from 'quill';
const Link = Quill.import('formats/link');
Link.sanitize = function (url: string) {
  // quill by default creates relative links if scheme is missing.
  if (!url.startsWith('http://') && !url.startsWith('https://')) {
    return `https://${url}`;
  }
  return url;
};
import ReactQuill from 'react-quill';

const EditModalContainer = styled.div`
  width: 600px;
  height: 700px;
`;

const BottomActionArea = styled.div`
  display: flex;
  justify-content: flex-end;
`;

type EmailFormVO = Omit<EmailVO, 'circulars' | 'type' | 'location' | 'country'> & {
  circulars: IOptionObj | null;
  type: IType | null;
  location: ILocation | null;
  country: string | null;
};

interface IProps {
  showModal: boolean;
  closeModal: () => void;
  refresh: () => void;
  email: EmailVO;
  mode: string;
}

const EmailModifierModal = ({ showModal, closeModal, email, mode, refresh }: IProps) => {
  const [form, setForm] = useState<EmailFormVO>({ ...initForm });
  const [enableCircular, setEnableCircular] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const countryMeta = useSelector((state: IWoorimInitState) => state.countries);

  useEffect(() => {
    if (mode === 'EDIT' && showModal) {
      setEnableCircular(false);
      getEmail(email.id).then((res) => {
        const circularLabel = circularOptions.find(({ id }) => id === res.circulars)?.label || '';
        const formData = {
          ...res,
          circulars: res.circulars ? { id: res.circulars, label: circularLabel } : null,
        };
        setForm(formData);
      });
    } else {
      setEnableCircular(false);
      const circularLabel = circularOptions.find(({ id }) => id === email.circulars)?.label || '';
      const formData = {
        ...email,
        circulars: email.circulars ? { id: email.circulars || '', label: circularLabel } : null,
        type: email.type || null,
        location: email.location || null,
        country: email.country || null,
      };

      setForm({ ...formData });
    }
  }, [email, showModal]);

  const setFormValue = (key: string, val: string | IOptionObj) => {
    if (key === 'country') {
      const area = countryMeta.find(({ code }) => code === val)?.area;
      if (area) setFormValue('area', area);
    }

    setForm((prev) => ({
      ...prev,
      [key]: val,
    }));
  };

  const doSave = async () => {
    const formData: EmailVO = {
      ...form,
      circulars: (form.circulars?.id as ICirculars) || '',
      type: form.type || '',
      location: form.location || '',
      country: form.country || '',
    };
    if (mode === 'EDIT') await putEmail(formData);
    else await postEmail(formData);
    await refresh();
    closeModal();
  };

  const doDelEmail = async () => {
    if (mode === 'EDIT') await delEmail([form.id]);
    await refresh();
    closeModal();
  };

  return (
    <CommonModal showModal={showModal} closeModal={closeModal}>
      <EditModalContainer>
        <Text $size={'20px'}>{mode}</Text>
        <Spacing $val={'12px'} />
        <FlexColumn $alignItems={'flex-start'}>
          <FormRow>
            <FormLabel>name</FormLabel>
            <CommonInput
              width={'300px'}
              value={form.name}
              changingValue={(val) => setFormValue('name', val)}
            />
          </FormRow>
          <FormRow>
            <FormLabel required>email</FormLabel>
            <CommonInput
              width={'300px'}
              value={form.email}
              changingValue={(val) => setFormValue('email', val)}
            />
          </FormRow>
          <FormRow>
            <FormLabel>company</FormLabel>
            <CommonInput
              width={'300px'}
              value={form.company}
              changingValue={(val) => setFormValue('company', val)}
            />
          </FormRow>
          <FormRow>
            <FormLabel required>country</FormLabel>
            <SelectCountry
              showLabel={false}
              multiple={false}
              selectedVal={form.country}
              selCountry={(val) => setFormValue('country', val as string)}
            />
          </FormRow>
          <FormRow>
            <FormLabel required>type</FormLabel>
            <CommonSelect2
              multiple={false}
              showLabel={false}
              selectedVal={form.type}
              selCountry={(val) => setFormValue('type', val as string)}
              options={typeOptions}
            />
          </FormRow>
          <FormRow>
            <FormLabel required>location</FormLabel>
            <CommonSelect2
              multiple={false}
              showLabel={false}
              selectedVal={form.location}
              selCountry={(val) => setFormValue('location', val as string)}
              options={locationOptions}
            />
          </FormRow>
          <FormRow>
            <FormLabel>area</FormLabel>
            <SelectArea
              disabled
              multiple={false}
              showLabel={false}
              selectedVal={form.area}
              selArea={(val) => setFormValue('area', val as string)}
            />
          </FormRow>
        </FlexColumn>
        <FormRow>
          <FormLabel>bespoke</FormLabel>
          <CommonInput
            width={'480px'}
            value={form.specialized}
            changingValue={(val) => setFormValue('specialized', val)}
          />
        </FormRow>
        <FormRow>
          <FormLabel>memo</FormLabel>
          <Flex $height={'178px'} $alignItems={'start'}>
            <ReactQuill
              style={{ height: '160px', width: '480px' }}
              value={form.memo || ''}
              onChange={(value) => setFormValue('memo', value)}
            />
          </Flex>
        </FormRow>
        <Spacing $val={'12px'} />
        <FormRow>
          <FormLabel>circulars</FormLabel>
          <CommonSelect2
            multiple={false}
            showLabel={false}
            selectedVal={form.circulars}
            selCountry={(val) => setFormValue('circulars', val as unknown as IOptionObj)}
            options={circularOptions}
            disabled={!enableCircular}
          />
          <CommonCheckbox val={enableCircular} doCheck={() => setEnableCircular((prev) => !prev)} />
        </FormRow>
        <FormRow>
          <FormLabel>grade</FormLabel>
          <CommonSelect2
            multiple={false}
            showLabel={false}
            selectedVal={form.grade}
            selCountry={(val) => setFormValue('grade', val as string)}
            options={gradeOptions}
            disabled={
              !enableCircular ||
              ['A-GRADE', 'B-GRADE', 'C-GRADE', 'D-GRADE'].includes(
                form.circulars?.id.toUpperCase() || '',
              )
            }
          />
        </FormRow>
      </EditModalContainer>
      <BottomActionArea>
        {mode === 'EDIT' && (
          <>
            <CommonButton label='DEL' onClick={() => setShowConfirm(true)} type='secondary' />
            <div style={{ width: '1px', backgroundColor: 'grey', margin: '4px 8px 4px 4px' }} />
          </>
        )}
        <CommonButton label='CANCEL' onClick={closeModal} type='secondary' />
        <CommonButton label='SAVE' onClick={doSave} />
      </BottomActionArea>
      <ConfirmModal
        showModal={showConfirm}
        closeModal={() => setShowConfirm(false)}
        msg={'삭제하시겠습니까?'}
        clickConfirm={doDelEmail}
      />
    </CommonModal>
  );
};

export default EmailModifierModal;

const FormRow = styled(Flex)`
  padding-bottom: 8px;
`;

const FormLabel = styled(Text)<{ required?: boolean }>`
  width: 100px;
  text-align: start;

  ${({ required }) =>
    required &&
    css`
      &:after {
        content: ' *';
        color: red;
      }
    `}
`;

const typeOptions = ['DIRECT', 'BROKERS', 'LOCALS'];
const locationOptions = ['WEST', 'EAST', 'WW'];

const circularOptions = [
  { id: 'A-GRADE', label: 'LOCAL-A' },
  { id: 'B-GRADE', label: 'LOCAL-B' },
  { id: 'C-GRADE', label: 'LOCAL-C' },
  { id: 'D-GRADE', label: 'LOCAL-D' },
  { id: 'POSITION', label: 'POSITION' },
  { id: 'ORDER', label: 'ORDER' },
  { id: 'BOTH', label: 'BOTH' },
];
const gradeOptions = ['AA', 'BB', 'CC'];

export const initForm: EmailFormVO = {
  id: 0,
  name: '',
  email: '',
  company: '',
  country: null,
  type: null,
  circulars: null,
  location: null,
  area: '',
  grade: '',
  specialized: '',
  remark: '',
  memo: '',
};
